/**
 * Shared filter control for the Scheduler surfaces — a compact button that
 * opens a searchable multi-select.
 *
 * Extracted from `FactoryScheduleView.view.tsx`, where it filters the factory
 * schedule's kitchen / line / product / base-material lists, so the input
 * tables can use the identical control rather than a second, differently
 * behaved one. Unchanged from its original: same tokens, same popover, same
 * select-all-visible semantics.
 */

import { memo, useMemo, useState } from 'react'
import { Box, Popover } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchIcon from '@mui/icons-material/Search'
import { FONT_MONO, FONT_SANS, RADII, TOKENS } from './SchedulerDesign'

// ─── TLFilterSelect ───────────────────────────────────────────────────────────

export type TLFilterOption = { value: string; label: string }

export const TLFilterSelect = memo(
  ({
    label,
    value,
    onChange,
    options,
  }: {
    label: string
    value: string[]
    onChange: (v: string[]) => void
    options: TLFilterOption[]
  }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [query, setQuery] = useState('')
    const open = Boolean(anchorEl)
    const active = value.length > 0

    const filteredOptions = useMemo(() => {
      const q = query.trim().toLowerCase()
      return q
        ? options.filter((o) => o.label.toLowerCase().includes(q))
        : options
    }, [options, query])

    const close = () => {
      setAnchorEl(null)
      setQuery('')
    }
    const toggleOption = (o: string) =>
      onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o])
    const allVisibleSelected =
      filteredOptions.length > 0 &&
      filteredOptions.every((o) => value.includes(o.value))

    return (
      <>
        <Box
          component="button"
          disabled={options.length === 0}
          onClick={(e: React.MouseEvent<HTMLElement>) =>
            setAnchorEl(anchorEl ? null : e.currentTarget)
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            height: 24,
            px: '9px',
            border: `1px solid ${active || open ? TOKENS.stateComplete : TOKENS.line300}`,
            borderRadius: RADII.sm,
            bgcolor: active ? 'rgba(14,124,134,0.08)' : '#fff',
            cursor: options.length === 0 ? 'not-allowed' : 'pointer',
            opacity: options.length === 0 ? 0.45 : 1,
            fontSize: 10.5,
            fontWeight: 600,
            fontFamily: FONT_SANS,
            color: active ? TOKENS.stateComplete : TOKENS.ink500,
            whiteSpace: 'nowrap',
            transition: 'border-color 0.12s, background-color 0.12s',
          }}
        >
          {label}
          {active && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 15,
                height: 15,
                px: '4px',
                borderRadius: RADII.md,
                bgcolor: TOKENS.stateComplete,
                color: '#fff',
                fontSize: 9,
                fontWeight: 700,
                fontFamily: FONT_MONO,
              }}
            >
              {value.length}
            </Box>
          )}
          <ExpandMoreIcon
            sx={{
              fontSize: 15,
              color: active ? TOKENS.stateComplete : TOKENS.ink400,
              transition: 'transform 0.15s',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        </Box>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              mt: '6px',
              width: 250,
              borderRadius: RADII.sm,
              border: `1px solid ${TOKENS.line200}`,
              boxShadow:
                '0 12px 32px rgba(14,27,34,0.18), 0 2px 8px rgba(14,27,34,0.08)',
              overflow: 'hidden',
            },
          }}
        >
          <Box sx={{ p: '12px 14px 10px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: '10px',
              }}
            >
              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: TOKENS.ink900,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontFamily: FONT_SANS,
                }}
              >
                {label}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {!allVisibleSelected && filteredOptions.length > 0 && (
                  <Box
                    component="button"
                    onClick={() =>
                      onChange(
                        Array.from(
                          new Set([
                            ...value,
                            ...filteredOptions.map((o) => o.value),
                          ])
                        )
                      )
                    }
                    sx={{
                      border: 'none',
                      bgcolor: 'transparent',
                      cursor: 'pointer',
                      p: 0,
                      fontSize: 10,
                      fontWeight: 700,
                      color: TOKENS.blue600,
                      fontFamily: FONT_SANS,
                    }}
                  >
                    Select all
                  </Box>
                )}
                {active && (
                  <Box
                    component="button"
                    onClick={() => onChange([])}
                    sx={{
                      border: 'none',
                      bgcolor: 'transparent',
                      cursor: 'pointer',
                      p: 0,
                      fontSize: 10,
                      fontWeight: 700,
                      color: TOKENS.ink400,
                      fontFamily: FONT_SANS,
                    }}
                  >
                    Clear
                  </Box>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '7px',
                height: 28,
                px: '9px',
                border: `1px solid ${TOKENS.line200}`,
                borderRadius: RADII.sm,
                bgcolor: TOKENS.surface50,
              }}
            >
              <SearchIcon sx={{ fontSize: 13, color: TOKENS.ink400 }} />
              <Box
                component="input"
                autoFocus
                value={query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setQuery(e.target.value)
                }
                placeholder={`Search ${label.toLowerCase()}…`}
                sx={{
                  border: 'none',
                  outline: 'none',
                  flex: 1,
                  minWidth: 0,
                  fontSize: 11,
                  fontFamily: FONT_SANS,
                  bgcolor: 'transparent',
                  color: TOKENS.ink900,
                  '&::placeholder': { color: TOKENS.ink400 },
                }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              maxHeight: 260,
              overflowY: 'auto',
              borderTop: `1px solid ${TOKENS.line100}`,
              py: '4px',
            }}
          >
            {filteredOptions.length === 0 ? (
              <Box
                sx={{
                  px: '14px',
                  py: '14px',
                  fontSize: 11,
                  color: TOKENS.ink400,
                  fontFamily: FONT_SANS,
                  textAlign: 'center',
                }}
              >
                No matches.
              </Box>
            ) : (
              filteredOptions.map((o) => {
                const checked = value.includes(o.value)
                return (
                  <Box
                    key={o.value}
                    component="button"
                    onClick={() => toggleOption(o.value)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '9px',
                      width: '100%',
                      height: 32,
                      px: '14px',
                      border: 'none',
                      bgcolor: 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background-color 0.1s',
                      '&:hover': { bgcolor: TOKENS.surface50 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 15,
                        height: 15,
                        borderRadius: RADII.sm,
                        border: `1.5px solid ${checked ? TOKENS.stateComplete : TOKENS.line300}`,
                        bgcolor: checked ? TOKENS.stateComplete : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'background-color 0.1s, border-color 0.1s',
                      }}
                    >
                      {checked && (
                        <CheckIcon sx={{ fontSize: 11, color: '#fff' }} />
                      )}
                    </Box>
                    <Box
                      sx={{
                        fontSize: 11.5,
                        fontWeight: checked ? 600 : 500,
                        color: TOKENS.ink900,
                        fontFamily: FONT_SANS,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {o.label}
                    </Box>
                  </Box>
                )
              })
            )}
          </Box>
        </Popover>
      </>
    )
  }
)
TLFilterSelect.displayName = 'TLFilterSelect'


// ─── TLSearchBox ──────────────────────────────────────────────────────────────

/**
 * Compact free-text filter box — search icon, bare input, clear button.
 *
 * Extracted from `FactoryScheduleView.view.tsx`'s factory-schedule toolbar for
 * the same reason `TLFilterSelect` was: the input tables needed the identical
 * control, and a second hand-rolled copy would drift from it. Unchanged from
 * the original markup and tokens, with the width and placeholder lifted to
 * props so a narrower toolbar can still use it.
 *
 * Deliberately uncontrolled-looking but fully controlled: the caller owns the
 * term, because the term also drives that surface's row filtering and row
 * counts.
 */
export const TLSearchBox = memo(
  ({
    value,
    onChange,
    placeholder = 'Filter…',
    width = 190,
  }: {
    value: string
    onChange: (next: string) => void
    placeholder?: string
    width?: number | string
  }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        height: 24,
        px: '8px',
        border: `1px solid ${TOKENS.line300}`,
        borderRadius: '2px',
        bgcolor: '#fff',
        width,
        flexShrink: 0,
      }}
    >
      <SearchIcon sx={{ fontSize: 13, color: TOKENS.ink400, flexShrink: 0 }} />
      <Box
        component="input"
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        placeholder={placeholder}
        sx={{
          border: 'none',
          outline: 'none',
          flex: 1,
          minWidth: 0,
          fontSize: 10.5,
          fontFamily: FONT_SANS,
          color: TOKENS.ink900,
          bgcolor: 'transparent',
          '&::placeholder': { color: TOKENS.ink400 },
        }}
      />
      {value && (
        <Box
          component="button"
          onClick={() => onChange('')}
          title="Clear filter"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            bgcolor: 'transparent',
            cursor: 'pointer',
            p: 0,
            color: TOKENS.ink400,
            flexShrink: 0,
          }}
        >
          <ClearIcon sx={{ fontSize: 12 }} />
        </Box>
      )}
    </Box>
  )
)
TLSearchBox.displayName = 'TLSearchBox'
